import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PERSONAL_CARE, ASSISTED_WITH, HOUSEKEEPING } from "@/lib/portalConstants";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = (clientId) => ({
  client_id: clientId || "",
  date: format(new Date(), "yyyy-MM-dd"),
  hours: "",
  personal_care: [],
  assisted_with: [],
  housekeeping: [],
  meals_hydration: "",
  wellbeing: "",
  summary: "",
  notes: "",
});

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
      active ? "bg-sage text-white border-sage" : "bg-white text-charcoal-soft border-charcoal/15 hover:border-sage"
    }`}
  >
    {children}
  </button>
);

export function CareLogDialog({ open, onOpenChange, clients, editLog, initialClientId, onSaved }) {
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editLog ? { ...empty(), ...editLog } : empty(initialClientId || clients[0]?.id || ""));
    }
  }, [open, editLog, initialClientId, clients]);

  const toggle = (field, value) =>
    setForm((f) => ({
      ...f,
      [field]: f[field].includes(value) ? f[field].filter((v) => v !== value) : [...f[field], value],
    }));

  const save = async () => {
    if (!form.client_id) return toast.error("Choose a client.");
    setSaving(true);
    try {
      const payload = { ...form, hours: form.hours === "" ? null : parseFloat(form.hours) };
      if (editLog) await api.put(`/portal/care-logs/${editLog.id}`, payload);
      else await api.post("/portal/care-logs", payload);
      toast.success(editLog ? "Care log updated" : "Care log saved");
      onOpenChange(false);
      onSaved?.();
    } catch {
      toast.error("Could not save care log.");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-sand max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="log-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{editLog ? "Edit Care Log" : "New Care Log"}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <Label>Client</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="log-client" className="mt-1.5 bg-white"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" data-testid="log-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5 bg-white" /></div>
            <div>
              <Label>Hours on site</Label>
              <Input type="number" min="0" step="0.5" data-testid="log-hours" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder="3" className="mt-1.5 bg-white" />
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Personal care — helped with</Label>
            <div className="flex flex-wrap gap-2">
              {PERSONAL_CARE.map((o) => <Chip key={o} testid={`pc-${o}`} active={form.personal_care.includes(o)} onClick={() => toggle("personal_care", o)}>{o}</Chip>)}
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Assisted with <span className="text-charcoal/40 text-xs">(per care plan)</span></Label>
            <div className="flex flex-wrap gap-2">
              {ASSISTED_WITH.map((o) => <Chip key={o} testid={`aw-${o}`} active={form.assisted_with.includes(o)} onClick={() => toggle("assisted_with", o)}>{o}</Chip>)}
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Housekeeping</Label>
            <div className="flex flex-wrap gap-2">
              {HOUSEKEEPING.map((o) => <Chip key={o} testid={`hk-${o}`} active={form.housekeeping.includes(o)} onClick={() => toggle("housekeeping", o)}>{o}</Chip>)}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Meals & hydration</Label><Input value={form.meals_hydration} onChange={(e) => setForm({ ...form, meals_hydration: e.target.value })} className="mt-1.5 bg-white" placeholder="Ate lunch, plenty of water" /></div>
            <div><Label>Wellbeing</Label><Input value={form.wellbeing} onChange={(e) => setForm({ ...form, wellbeing: e.target.value })} className="mt-1.5 bg-white" placeholder="Good mood, rested well" /></div>
          </div>
          <div>
            <Label>Summary for family <span className="text-charcoal/40 text-xs">(they'll see this)</span></Label>
            <Textarea data-testid="log-summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="mt-1.5 bg-white resize-none" rows={2} placeholder="A warm recap of the day…" />
          </div>
          <div>
            <Label>Private notes <span className="text-charcoal/40 text-xs">(only you)</span></Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 bg-white resize-none" rows={2} />
          </div>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-log" className="mt-2 w-full rounded-full bg-sage text-white py-3 font-medium hover:bg-sage-dark transition-colors disabled:opacity-60">
          {saving ? "Saving…" : editLog ? "Save Changes" : "Save Care Log"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
